import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { CostCenter } from './cost-center.model';
import { CostCenterService } from './cost-center.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from 'src/app/security/permission.service';
import { TagModule } from 'primeng/tag';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';

@Component({
    selector: 'app-cost-center',
    templateUrl: './cost-center.component.html',
    styleUrls: ['./cost-center.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ConfirmDialogModule,
        TextareaModule,
        CheckboxModule,
        ToastModule,
        RippleModule,
        ProgressBarModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        TooltipModule,
        TagModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        CostCenterService,
        MessageService,
        ConfirmationService,
        SessionStorageService
    ]

})
export class CostCenterComponent implements OnInit {

    @ViewChild('dt') dt: Table | undefined; // For global filter

    isLoading: boolean = false;

    costCenters: CostCenter[] = [];
    costCenterDialog: boolean = false;
    costCenterForm: FormGroup;
    selectedCostCenter: CostCenter | null = null; // For edit mode

    canAddCostCenter = false;
    canEditCostCenter = false;
    canDeleteCostCenter = false;
    canSaveCostCenter = false;

    constructor(
        private fb: FormBuilder,
        private costCenterService: CostCenterService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService, // Inject ConfirmationService
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService
    ) {
        this.costCenterForm = this.fb.group({
            id: [null],
            code: ['', [Validators.required, Validators.maxLength(20)]],
            name: ['', [Validators.required, Validators.maxLength(100)]],
            description: [''],
            isActive: [true] // Default to active
        });
    }

    ngOnInit(): void {
        this.canAddCostCenter = this.permissionService.hasPermission(app_permissions.accounts.cost_center.create);
        this.canEditCostCenter = this.permissionService.hasPermission(app_permissions.accounts.cost_center.edit);
        this.canDeleteCostCenter = this.permissionService.hasPermission(app_permissions.accounts.cost_center.delete);
        this.canSaveCostCenter = this.permissionService.hasAnyPermission([app_permissions.accounts.cost_center.create, app_permissions.accounts.cost_center.edit]);

        this.loadCostCenters();
    }

    loadCostCenters(): void {
        this.isLoading = true;
        const owningCompanyId = this.sessionStorageService.getCompany()?.id;

        if (!owningCompanyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Owning Company ID not found.' });
            this.isLoading = false;
            return;
        }

        this.costCenterService.getAllCostCenters(owningCompanyId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (data) => {
                    this.costCenters = data;
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load cost centers.' });
                    console.error('Error loading cost centers:', err);
                }
            });
    }

    openNew(): void {
        this.selectedCostCenter = null;
        this.costCenterForm.reset({ isActive: true }); // Reset form and set isActive to true
        this.costCenterDialog = true;
    }

    hideDialog(): void {
        this.costCenterDialog = false;
        this.selectedCostCenter = null;
    }

    saveCostCenter(): void {
        if (this.costCenterForm.invalid) {
            this.costCenterForm.markAllAsTouched(); // Mark all fields as touched to show validation errors
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please fill all required fields correctly.' });
            return;
        }

        this.isLoading = true;
        const costCenterData: CostCenter = this.costCenterForm.value;
        const owningCompanyId = this.sessionStorageService.getCompany()?.id;

        if (!owningCompanyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Owning Company ID not found.' });
            this.isLoading = false;
            return;
        }
        costCenterData.owningCompanyId = owningCompanyId; // Assign owning company ID

        let save$: Observable<CostCenter>;

        if (costCenterData.id) {
            // Update existing
            save$ = this.costCenterService.updateCostCenter(costCenterData);
        } else {
            // Create new
            save$ = this.costCenterService.createCostCenter(costCenterData);
        }

        save$.pipe(finalize(() => this.isLoading = false)).subscribe({
            next: (res) => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Cost Center saved successfully!' });
                this.hideDialog();
                this.loadCostCenters(); // Reload data
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save cost center: ' + (err.error?.message || err.message) });
                console.error('Save cost center error:', err);
            }
        });
    }

    editCostCenter(costCenter: CostCenter): void {
        this.selectedCostCenter = { ...costCenter }; // Create a copy
        this.costCenterForm.patchValue(this.selectedCostCenter);
        this.costCenterDialog = true;
    }

    confirmDelete(costCenter: CostCenter): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete cost center "${costCenter.name}"? This action cannot be undone.`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.deleteCostCenter(costCenter.id!);
            }
        });
    }

    deleteCostCenter(id: number): void {
        this.isLoading = true;
        this.costCenterService.deleteCostCenter(id)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Cost Center deleted successfully!' });
                    this.loadCostCenters(); // Reload data
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete cost center: ' + (err.error?.message || err.message) });
                    console.error('Delete cost center error:', err);
                }
            });
    }

    // Helper for PrimeNG table global filter
    applyFilterGlobal($event: any, stringVal: string) {
        this.dt?.filterGlobal(($event.target as HTMLInputElement).value, stringVal);
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Cost Centers', value: this.costCenters.length, hint: 'Configured dimensions', tone: 'info' },
            { label: 'Active', value: this.costCenters.filter(costCenter => costCenter.isActive).length, tone: 'success' },
            { label: 'Inactive', value: this.costCenters.filter(costCenter => costCenter.isActive === false).length, tone: 'warn' },
            { label: 'Editable', value: this.costCenters.length, hint: 'Finance setup', tone: 'default' }
        ];
    }

    get dialogTitle(): string {
        return this.selectedCostCenter ? 'Edit Cost Center' : 'New Cost Center';
    }

    get dialogSubtitle(): string {
        return this.selectedCostCenter
            ? 'Update the accounting dimension used for reporting and allocation.'
            : 'Create a reporting dimension for branch, warehouse, route, department, or operations team.';
    }

    statusSeverity(costCenter: CostCenter): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        return costCenter.isActive === false ? 'secondary' : 'success';
    }
}
